"use client";

import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { ActionLink } from "@/components/common/actions/actions.component";
import { useAudience } from "@/components/common/audience";
import { getI18nString } from "@/lib/utils";
import type { FooterFragmentResult } from "@/lib/sanity/sanity.types";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@repo/ui/accordion";

const SanityForm = dynamic(() =>
  import("@/components/form/sanity-form.component").then(
    (mod) => mod.SanityForm,
  ),
);

// Extract types from FooterFragmentResult
type FooterData = NonNullable<FooterFragmentResult>;

interface FooterMenuMobileProps {
  form: FooterData["form"];
  menus: FooterData["menus"];
  businessMenus: FooterData["businessMenus"];
}

/**
 * Mobile footer with accordion pattern.
 * Newsletter form and menu sections are collapsible.
 */
export function FooterMenuMobile({
  form,
  menus,
  businessMenus,
}: FooterMenuMobileProps): JSX.Element {
  const { activeTab } = useAudience();
  const t = useTranslations("footer");
  const activeMenus = activeTab === "businesses" ? businessMenus : menus;
  return (
    <Accordion type="single" collapsible className="space-y-6 pb-6">
      {/* Newsletter Form Accordion */}
      {form?._id && (
        <AccordionItem
          value="newsletter"
          className="border-b first:border-t-0 border-border-subtle pb-6"
        >
          <AccordionTrigger className="container-grid py-1 typography-component-xs-regular text-text-primary [&>svg]:text-text-primary">
            {t("newsletterHeading")}
          </AccordionTrigger>
          <AccordionContent>
            <div className="container-grid pb-6">
              <div className="col-span-full">
                <SanityForm formData={form} hideGroupHeadlines compact />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {/* Menu Accordions */}
      {activeMenus?.map((menu) =>
        menu ? (
          <AccordionItem
            key={menu.id}
            value={menu.id}
            className="border-b-0 first:border-t-0"
          >
            <AccordionTrigger className="container-grid py-1 typography-mobile-heading-overline text-text-primary [&>svg]:text-text-primary">
              {menu.heading}
            </AccordionTrigger>
            <AccordionContent>
              <div className="container-grid pb-6">
                <ul className="col-span-full space-y-3">
                  {menu.items?.map((item) =>
                    item?.to ? (
                      <li key={item._id}>
                        <ActionLink
                          actionTo={item.to}
                          className="typography-component-xs-regular text-text-secondary border-b border-transparent transition-colors hover:border-current"
                        >
                          {getI18nString(item.text, "Link")}
                        </ActionLink>
                      </li>
                    ) : null,
                  )}
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>
        ) : null,
      )}
    </Accordion>
  );
}
